import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner, faUpload } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import apiServices from '../../../../../services/apiService';
import { jwtDecode } from "jwt-decode";
import storageService from '../../../../../utils/storageService';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';
import DeleteModal from "../../../../common/commonmodal/DeleteModal";

const InviteGuestForm = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const [sites, setSites] = useState([]);
    const [units, setUnits] = useState([]);
    const [filePreview, setFilePreview] = useState(null);
    const [loggedInUserRole, setLoggedInUserRole] = useState("");
    const [isBlockedModalOpen, setIsBlockedModalOpen] = useState(false);

    const [formData, setFormData] = useState({
        siteId: null,
        unitId: null,
        visitorName: '',
        visitorLocation: '',
        mobileNumber: '',
        emailId: '',
        purposeOfVisit: '',
        additionalCount: '',
        entryDate: '',
        exitDate: '',
        image: null
    });

    const decodedTokenData = React.useMemo(() => {
        const rawToken = storageService.getItem('Token');
        if (!rawToken) return null;
        try {
            return jwtDecode(rawToken);
        } catch (e) {
            console.error("Failed to decode token", e);
            return null;
        }
    }, []);

    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                const tokenUserId = decodedTokenData?.id;

                if (tokenUserId) {
                    const userRes = await apiServices({
                        method: 'GET',
                        url: `/CommUserDetails/getUserDetails?Id=${tokenUserId}&PageNumber=1&PageSize=10`
                    });
                    
                    const isSuccess = userRes?.success === true || userRes?.data?.success === true;
                    if (isSuccess) {
                        const resData = userRes?.data?.Commuserdetailsdata || userRes?.data?.data || userRes?.data || [];
                        const userData = Array.isArray(resData) ? resData[0] : resData;
                        
                        if (userData) {
                            const userSiteId = userData.siteId;
                            const userSiteName = userData.SiteName;
                            const userUnitId = userData.UnitId;
                            const userUnitName = userData.unitName;
                            const roleName = userData.RoleName || userData.roleName || "";

                            setLoggedInUserRole(roleName);

                            if (userSiteId) {
                                setSites([{ label: userSiteName, value: userSiteId }]);
                            }

                            const isSiteAdmin = roleName.toLowerCase().includes('site admin') || roleName.toLowerCase().includes('super');

                            if (isSiteAdmin) {
                                try {
                                    const unitRes = await apiServices({ method: "GET", url: "/UnitMaster/GetAllUnitMaster" });
                                    if (unitRes?.success || unitRes?.IsSuccess) {
                                        const data = unitRes.data?.data || unitRes.data || [];
                                        const formatted = data.map(u => ({
                                            label: `${u.UnitName} (${u.UnitNumber})`,
                                            value: u.UnitId,
                                            siteId: u.SiteId || userSiteId
                                        }));
                                        setUnits(formatted);
                                    }
                                } catch (error) {
                                    console.error("Error fetching units:", error);
                                }
                            } else if (userUnitId) {
                                setUnits([{ label: userUnitName, value: userUnitId, siteId: userSiteId }]);
                            }

                            setFormData(prev => ({
                                ...prev,
                                hostName: `${userData.FirstName || ''} ${userData.LastName || ''}`.trim() || prev.hostName,
                                hostid: userData.Id || prev.hostid,
                                siteId: userSiteId || prev.siteId,
                                unitId: isSiteAdmin ? null : (userUnitId || prev.unitId)
                            }));
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching master data:", error);
            }
        };
        fetchMasterData();
    }, []);

    const checkVisitorByMobile = async (mobile) => {
        try {
            setIsLoading(true);
            const response = await apiServices({
                method: "GET",
                url: `/Visitor/getAllVisitorByMobileNumber/${mobile}`
            });
            const latestVisitor = response?.data?.data?.[0];
            if (response?.success && latestVisitor) {
                const isBlocked = latestVisitor?.status?.toLowerCase() === "block" ||
                    latestVisitor?.status?.toLowerCase() === "blocked" ||
                    latestVisitor?.isActive?.toLowerCase() === "inactive";

                if (isBlocked) {
                    setIsBlockedModalOpen(true);
                } else {
                    setFormData(prev => ({
                        ...prev,
                        visitorName: latestVisitor.visitorName || "",
                        visitorLocation: latestVisitor.location || "",
                        emailId: latestVisitor.emailId || "",
                        purposeOfVisit: latestVisitor.purposeOfVisit || "",
                        additionalCount: latestVisitor.additionalCount || 0,
                        image: latestVisitor.image || null
                    }));
                    if (latestVisitor.image) {
                        setFilePreview(latestVisitor.image);
                    }
                    toast.info("Visitor details auto-filled from previous visit.");
                }
            }
        } catch (error) {
            console.error("Error checking visitor by mobile:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseBlockedModal = () => {
        setIsBlockedModalOpen(false);
        setFormData({
            siteId: formData.siteId,
            unitId: formData.unitId,
            visitorName: '',
            visitorLocation: '',
            mobileNumber: '',
            emailId: '',
            purposeOfVisit: '',
            additionalCount: '',
            entryDate: formData.entryDate,
            exitDate: formData.exitDate,
            image: null
        });
        setFilePreview(null);
    };

    const handleInputChange = async (e) => {
        const { id, value } = e.target;
        if (id === 'mobileNumber') {
            const cleanedValue = value.replace(/\D/g, '').slice(0, 10);
            setFormData(prev => ({ ...prev, [id]: cleanedValue }));
            if (cleanedValue.length === 10) {
                await checkVisitorByMobile(cleanedValue);
            }
        } else {
            setFormData(prev => ({ ...prev, [id]: value }));
        }
    };

    const handleSelectChange = (key, option) => {
        setFormData(prev => {
            const newData = { ...prev, [key]: option ? option.value : null };
            if (key === 'siteId') {
                newData.unitId = null;
            }
            return newData;
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({ ...prev, image: reader.result }));
                setFilePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const validateForm = () => {
        let newErrors = {};

        if (!formData.siteId) newErrors.siteId = "Site Name is required";
        if (!formData.unitId) newErrors.unitId = "Unit Name is required";
        if (!formData.visitorName || !formData.visitorName.trim()) newErrors.visitorName = "Visitor Name is required";
        if (!formData.visitorLocation || !formData.visitorLocation.trim()) newErrors.visitorLocation = "Coming From location is required";

        if (!formData.mobileNumber || !formData.mobileNumber.trim()) {
            newErrors.mobileNumber = "Mobile Number is required";
        } else if (formData.mobileNumber.length !== 10) {
            newErrors.mobileNumber = "Mobile number must be exactly 10 digits";
        }

        if (!formData.purposeOfVisit || !formData.purposeOfVisit.trim()) newErrors.purposeOfVisit = "Reason For Visit is required";
        if (!formData.entryDate) newErrors.entryDate = "Expected Entry Time is required";

        if (!formData.exitDate) {
            newErrors.exitDate = "Stay Until time is required";
        } else if (new Date(formData.exitDate) < new Date(formData.entryDate)) {
            newErrors.exitDate = "Exit time cannot be before entry time";
        }

        if (!formData.image) newErrors.image = "Visitor Image is required";

        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            toast.warning("Please fill all required fields correctly.");
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        // Ensure image is plain base64 without data URI prefix
        let plainBase64Image = formData.image || null;
        if (plainBase64Image && plainBase64Image.includes(',')) {
            plainBase64Image = plainBase64Image.split(',')[1];
        }

        const payload = {
            siteId: parseInt(formData.siteId) || 0,
            unitId: parseInt(formData.unitId) || 0,
            visitorName: formData.visitorName,
            visitorLocation: formData.visitorLocation,
            visitorMobileNumber: formData.mobileNumber,
            additionalCount: parseInt(formData.additionalCount) || 0,
            purposeOfVisit: formData.purposeOfVisit,
            visitorEmailId: formData.emailId,
            expectedEntryTime: formData.entryDate,
            expectedExitTime: formData.exitDate,
            userId: parseInt(decodedTokenData?.id) || 0,
            image: plainBase64Image,
            hostName: decodedTokenData?.name || ""
        };
        console.log(payload, "invite");

        try {
            const response = await apiServices({
                method: "POST",
                url: "/InviteVisitor/postInvitedVisitor",
                data: payload,
            });
            console.log(response, "invite response")

            if (response.success) {
                toast.success("Visitor added successfully!");
                navigate('/zentcomm/visitor-managment/invited-visitor');
            } else {
                toast.error(response.message || "Failed to add visitor.");
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("An error occurred during submission.");
        } finally {
            setIsLoading(false);
        }
    };

    const filteredUnits = units.filter(u => !formData.siteId || String(u.siteId) === String(formData.siteId));

    // Get today's datetime string for min values
    const now = new Date();
    const tzOffset = now.getTimezoneOffset() * 60000;
    const localISOTime = (new Date(now - tzOffset)).toISOString().slice(0, 16);

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs />

            <PageHeadingSection
                Navbar={true}
                mainHeading="Add New Visitor"
                subHeading="Invite a visitor by providing visitor details and visit schedule"
            />

            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="flex flex-col flex-1">
                <div className="mt-4 bg-sectionheaderbg p-6 rounded-2xl border border-gray-100 dark:border-bordercolor flex flex-col gap-6 shadow-sm mb-4">

                    {/* Site Information */}
                    <div>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary mb-3 pb-2 border-b dark:border-bordercolor">Site Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <CustomSelect
                                    label={<>Site Name <span className="text-red-500">*</span></>}
                                    options={sites}
                                    value={sites.find(s => String(s.value) === String(formData.siteId)) || null}
                                    onChange={(opt) => handleSelectChange('siteId', opt)}
                                    placeholder="Select Site"
                                />
                                {errors.siteId && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.siteId}</p>}
                            </div>

                            <div>
                                <CustomSelect
                                    label={<>Unit Name <span className="text-red-500">*</span></>}
                                    options={filteredUnits}
                                    value={filteredUnits.find(u => String(u.value) === String(formData.unitId)) || null}
                                    onChange={(opt) => handleSelectChange('unitId', opt)}
                                    placeholder="Select Unit"
                                    isDisabled={!(loggedInUserRole?.toLowerCase().includes("site admin") || loggedInUserRole?.toLowerCase().includes("super")) || !formData.siteId}
                                />
                                {errors.unitId && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.unitId}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Visitor Information */}
                    <div>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary mb-3 pb-2 border-b dark:border-bordercolor">Visitor Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="mobileNumber">
                                    Mobile Number <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full px-3 py-2 border rounded-md text-sm outline-none bg-transparent text-textprimary transition-colors ${errors.mobileNumber ? 'border-red-500 focus:border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:border-indigo-500'}`}
                                    type="tel"
                                    id="mobileNumber"
                                    value={formData.mobileNumber}
                                    onChange={handleInputChange}
                                    placeholder="Enter Mobile Number"
                                    maxLength="10"
                                />
                                {errors.mobileNumber && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.mobileNumber}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="visitorName">
                                    Visitor Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full px-3 py-2 border rounded-md text-sm outline-none bg-transparent text-textprimary transition-colors ${errors.visitorName ? 'border-red-500 focus:border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:border-indigo-500'}`}
                                    type="text"
                                    id="visitorName"
                                    value={formData.visitorName}
                                    onChange={handleInputChange}
                                    placeholder="Enter Visitor Name"
                                />
                                {errors.visitorName && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.visitorName}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="visitorLocation">
                                    Coming From <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full px-3 py-2 border rounded-md text-sm outline-none bg-transparent text-textprimary transition-colors ${errors.visitorLocation ? 'border-red-500 focus:border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:border-indigo-500'}`}
                                    type="text"
                                    id="visitorLocation"
                                    value={formData.visitorLocation}
                                    onChange={handleInputChange}
                                    placeholder="Enter Location Name"
                                />
                                {errors.visitorLocation && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.visitorLocation}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="emailId">
                                    Email ID
                                </label>
                                <input
                                    className={`w-full px-3 py-2 border rounded-md text-sm outline-none bg-transparent text-textprimary transition-colors ${errors.emailId ? 'border-red-500 focus:border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:border-indigo-500'}`}
                                    type="email"
                                    id="emailId"
                                    value={formData.emailId}
                                    onChange={handleInputChange}
                                    placeholder="Enter Email Address"
                                />
                                {errors.emailId && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.emailId}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Visit Details */}
                    <div>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary mb-3 pb-2 border-b dark:border-bordercolor">Visit Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="purposeOfVisit">
                                    Reason For Visit <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full px-3 py-2 border rounded-md text-sm outline-none bg-transparent text-textprimary transition-colors ${errors.purposeOfVisit ? 'border-red-500 focus:border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:border-indigo-500'}`}
                                    type="text"
                                    id="purposeOfVisit"
                                    value={formData.purposeOfVisit}
                                    onChange={handleInputChange}
                                    placeholder="Enter reason for visit"
                                />
                                {errors.purposeOfVisit && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.purposeOfVisit}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="additionalCount">
                                    Additional count
                                </label>
                                <input
                                    className={`w-full px-3 py-2 border rounded-md text-sm outline-none bg-transparent text-textprimary transition-colors ${errors.additionalCount ? 'border-red-500 focus:border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:border-indigo-500'}`}
                                    type="number"
                                    id="additionalCount"
                                    value={formData.additionalCount}
                                    onChange={handleInputChange}
                                    placeholder="Enter additional count"
                                    min="0"
                                />
                                {errors.additionalCount && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.additionalCount}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Schedule Information */}
                    <div>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary mb-3 pb-2 border-b dark:border-bordercolor">Schedule Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="entryDate">
                                    Expected Entry Time <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full px-3 py-2 border rounded-md text-sm outline-none bg-transparent text-textprimary transition-colors ${errors.entryDate ? 'border-red-500 focus:border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:border-indigo-500'}`}
                                    type="datetime-local"
                                    id="entryDate"
                                    value={formData.entryDate}
                                    onChange={handleInputChange}
                                    min={localISOTime}
                                />
                                {errors.entryDate && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.entryDate}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="exitDate">
                                    Stay Until <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full px-3 py-2 border rounded-md text-sm outline-none bg-transparent text-textprimary transition-colors ${errors.exitDate ? 'border-red-500 focus:border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:border-indigo-500'}`}
                                    type="datetime-local"
                                    id="exitDate"
                                    value={formData.exitDate}
                                    onChange={handleInputChange}
                                    min={formData.entryDate || localISOTime}
                                />
                                {errors.exitDate && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.exitDate}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Visitor Image */}
                    <div>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary mb-3 pb-2 border-b dark:border-bordercolor">Visitor Image</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                    Upload Image <span className="text-red-500">*</span>
                                </label>
                                <div className="flex items-center gap-4">
                                    <label className={`cursor-pointer flex items-center justify-center px-4 py-2 border rounded-md shadow-sm text-xs font-bold bg-transparent text-textprimary transition-colors ${errors.image ? 'border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-actionitembg' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400 hover:bg-gray-50 dark:hover:bg-actionitembg'}`}>
                                        <FontAwesomeIcon icon={faUpload} className="mr-2" />
                                        Choose File
                                        <input type="file" accept=".png,.jpg,.jpeg" className="hidden" onChange={handleFileChange} />
                                    </label>
                                    <span className="text-[10px] text-gray-500 dark:text-textsecondary">Only PNG, JPG, JPEG files are allowed</span>
                                </div>
                                {errors.image && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.image}</p>}
                                {filePreview && (
                                    <div className="mt-3">
                                        <img src={filePreview} alt="Preview" className="h-20 w-20 object-cover rounded-md border border-gray-200 dark:border-bordercolor" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                </div>

                <div className="flex justify-between mt-auto border-t dark:border-bordercolor pt-4">
                    <button
                        type="button"
                        onClick={() => navigate("/zentcomm/visitor-managment/invited-visitor")}
                        className="px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-xs flex items-center gap-2 text-gray-600 dark:text-textsecondary hover:bg-gray-50 dark:hover:bg-actionitembg transition-colors"
                    >
                        <FontAwesomeIcon icon={faAngleLeft} />
                        Cancel
                    </button>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                        style={{ backgroundColor: "var(--color-secondaryaccent)", color: "var(--color-primaryaccent)" }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "var(--color-primaryaccent)";
                            e.currentTarget.style.color = '#FFFFFF';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "var(--color-secondaryaccent)";
                            e.currentTarget.style.color = "var(--color-primaryaccent)";
                        }}
                    >
                        {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Submit'}
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                </div>
            </form>

            {/* Blocked Visitor Warning Modal */}
            <DeleteModal
                isOpen={isBlockedModalOpen}
                onClose={handleCloseBlockedModal}
                onConfirm={handleCloseBlockedModal}
                title="Visitor Blocked"
                description="This visitor has been blacklisted/blocked and is not allowed to register or enter the premises. Please contact the administrator."
                confirmBtnText="OK"
                cancelBtnText="Close"
                isLoading={false}
            />
        </div>
    );
};

export default InviteGuestForm;
